import { z } from 'zod'

export type SearchEngine = 'unified' | 'agentic' | 'documentChat'

export const SearchSourceSchema = z.enum(['elasticsearch', 'pinecone'])

export const HighlightSchema = z.strictObject({
  page_num: z.number(),
  page_id: z.string().nullable().optional(),
  fragments: z.array(z.string()),
})

export const SearchResultDocumentSchema = z.strictObject({
  document_id: z.string(),
  score: z.number(),
  sources: z.array(SearchSourceSchema),
  elasticsearch_score: z.number().nullable().optional(),
  pinecone_score: z.number().nullable().optional(),
  highlights: z.array(HighlightSchema).nullable().optional(),
})

export const UnifiedSearchRequestSchema = z.strictObject({
  query: z.string(),
  site_id: z.string(),
  limit: z.number().int().min(1).max(200).optional(),
  preview_mode: z.boolean().optional(),
})

export const AgenticSearchRequestSchema = UnifiedSearchRequestSchema.extend({
  conversation_id: z.string().optional(),
})

export const UnifiedDocumentSearchResultSchema = z.strictObject({
  query: z.string(),
  site_id: z.string(),
  weights: z.strictObject({
    lexical_weight: z.number(),
    semantic_weight: z.number(),
  }),
  results: z.array(SearchResultDocumentSchema),
})

export const PageTextCitationSchema = z.strictObject({
  type: z.literal('page_text'),
  document_id: z.string(),
  page_id: z.string(),
  page_num: z.number(),
  quote: z.string(),
})

export const ResponseSegmentSchema = z.strictObject({
  text: z.string(),
  citations: z.array(PageTextCitationSchema),
})

export const AgenticQueryPlanSchema = z.looseObject({
  elasticsearch_query: z.string().min(1),
  pinecone_query: z.string().min(1),
  elasticsearch_weight: z.number().min(0).max(1),
  pinecone_weight: z.number().min(0).max(1),
  final_response: z.string().min(1),
})

export const AgenticSearchMetadataSchema = z.strictObject({
  query_plan: AgenticQueryPlanSchema,
  is_fallback: z.boolean(),
})

export const AgenticSearchResponseEventSchema = z
  .strictObject({
    type: z.literal('response'),
    query: z.string(),
    site_id: z.string(),
    conversation_id: z.string(),
    final_response: z.string().optional(),
    response: z.string().optional(),
    response_segments: z.array(ResponseSegmentSchema).optional(),
    metadata: AgenticSearchMetadataSchema,
    results: z.array(SearchResultDocumentSchema),
  })
  .refine(
    (data) => {
      const hasFinalResponse =
        data.final_response !== undefined &&
        data.final_response.trim().length > 0
      const hasResponse =
        data.response !== undefined && data.response.trim().length > 0
      const hasSegmentText =
        data.response_segments !== undefined &&
        data.response_segments.some((segment) => segment.text.trim().length > 0)
      return hasFinalResponse || hasResponse || hasSegmentText
    },
    {
      error:
        'Response event requires a non-empty final_response, response, or response_segments text',
    }
  )

export const AgenticSearchThoughtEventSchema = z.strictObject({
  type: z.literal('thought'),
  query: z.string(),
  site_id: z.string(),
  conversation_id: z.string(),
  message: z.string().min(1),
})

export const AgenticSearchErrorEventSchema = z.strictObject({
  type: z.literal('error'),
  query: z.string(),
  site_id: z.string(),
  conversation_id: z.string().nullable().optional(),
  message: z.string().min(1),
})

export const AgenticSearchSseEventSchema = z.union([
  AgenticSearchThoughtEventSchema,
  AgenticSearchResponseEventSchema,
  AgenticSearchErrorEventSchema,
])

export type Highlight = z.infer<typeof HighlightSchema>
export type PageTextCitation = z.infer<typeof PageTextCitationSchema>
export type ResponseSegment = z.infer<typeof ResponseSegmentSchema>
export type SearchResultDoc = z.infer<typeof SearchResultDocumentSchema>
export type UnifiedDocumentSearchResult = z.infer<
  typeof UnifiedDocumentSearchResultSchema
>
export type AgenticSearchMetadata = z.infer<typeof AgenticSearchMetadataSchema>
export type AgenticSearchThoughtEvent = z.infer<
  typeof AgenticSearchThoughtEventSchema
>
export type AgenticSearchErrorEvent = z.infer<
  typeof AgenticSearchErrorEventSchema
>
export type AgenticSearchSseEvent = z.infer<typeof AgenticSearchSseEventSchema>
export type AgenticDocumentSearchResult = z.infer<
  typeof AgenticSearchResponseEventSchema
>
export type DocumentSearchResult =
  | UnifiedDocumentSearchResult
  | AgenticDocumentSearchResult

export function parseAgenticSearchSseData(
  rawData: string
): AgenticSearchSseEvent {
  const parsed: unknown = JSON.parse(rawData)
  return AgenticSearchSseEventSchema.parse(parsed)
}

export function isAgenticDocumentSearchResult(
  result: DocumentSearchResult | undefined
): result is AgenticDocumentSearchResult {
  return !!result && 'type' in result && result.type === 'response'
}

export function getDocumentSearchWeights(
  result: DocumentSearchResult | undefined
):
  | {
      lexical_weight: number
      semantic_weight: number
    }
  | undefined {
  if (!result) return undefined
  if (isAgenticDocumentSearchResult(result)) {
    return {
      lexical_weight: result.metadata.query_plan.elasticsearch_weight,
      semantic_weight: result.metadata.query_plan.pinecone_weight,
    }
  }
  return result.weights
}
